import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router';
import { ChevronDown } from 'lucide-react';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { ActionHintIcon } from '#/modules/commonComponents/hint';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import {
  buildManufacturingMaterialsUrlFromCustomerOrderLine,
} from '#/modules/crmMgmt/customerOrder/helper/buildManufacturingMaterialsUrlFromCustomerOrderLine';
import {
  buildPurchaseOrderCreateUrlFromMaterialSnapshots,
} from '#/modules/crmMgmt/customerOrder/helper/buildPurchaseOrderCreateUrlFromMaterialSnapshots';
import {
  buildWorkOrderCreateUrlFromMaterialSnapshot,
} from '#/modules/crmMgmt/customerOrder/helper/buildWorkOrderCreateUrlFromMaterialSnapshot';
import {
  canCreateManufacturingWorkOrdersFromCustomerOrder,
} from '#/modules/crmMgmt/customerOrder/helper/customerOrderStatus';
import {
  isManufacturableMaterialSnapshot,
  isPurchasableMaterialSnapshot,
  resolveMaterialSnapshotMtrId,
} from '#/modules/crmMgmt/customerOrder/helper/materialSnapshotCapabilities';
import {
  displayOrDash,
  getCustomerOrderLineCancelQty,
  getCustomerOrderLineDeliveredQty,
  getCustomerOrderLineDiscountRate,
  getCustomerOrderLineDiscountTotalAmount,
  getCustomerOrderLineRoundingAdjustment,
  getCustomerOrderLineUnitDiscountAmount,
  getCustomerOrderLineMtrRequirements,
  getCustomerOrderLineOrderQty,
  getCustomerOrderLineRemainingDeliveryQty,
  getCustomerOrderLineRemainingQty,
  getCustomerOrderLineSalesOrderQty,
  getCustomerOrderLineSubtotalAmount,
  getCustomerOrderLineUnitPrice,
  getExtraItemDeliveredQty,
  getExtraItemQtyPerProduct,
  getExtraItemReferenceQty,
  getExtraItemRemainingReferenceQty,
  getMaterialSnapshotBaseUnitName,
  getMaterialSnapshotMtrName,
  getMaterialSnapshotMtrNo,
  getMaterialSnapshotQtyPerProduct,
} from '#/modules/crmMgmt/customerOrder/helper/mapCustomerOrderMeta';
import {
  formatDisplayAmount,
  formatDisplayQty,
} from '#/utils/formatNumber';

function MiniTable({ headers, headerCells, rows, emptyText }) {
  if (!rows || rows.length === 0) {
    return <p className="px-1 py-2 text-sm text-muted-foreground">{emptyText}</p>;
  }

  return (
    <div className="overflow-x-auto rounded border border-slate-200">
      <table className="w-full min-w-[36rem] text-left text-sm">
        <thead className="bg-slate-100 text-xs text-gray-600">
          <tr>
            {headers.map((header, index) => (
              <th
                key={`${header}-${index}`}
                className="px-2 py-1.5 font-medium whitespace-nowrap"
              >
                {headerCells?.[index] ?? header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-slate-200">
              {row.cells.map((cell, index) => (
                <td
                  key={`${row.id}-${headers[index]}`}
                  className="px-2 py-1.5 text-gray-900 whitespace-nowrap"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

MiniTable.propTypes = {
  headers: PropTypes.arrayOf(PropTypes.string).isRequired,
  headerCells: PropTypes.arrayOf(PropTypes.node),
  rows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    cells: PropTypes.arrayOf(PropTypes.node).isRequired,
  })),
  emptyText: PropTypes.string.isRequired,
};

const METRIC_TONE_CLASS = {
  highlight: 'text-sky-600',
  alert: 'text-red-600',
  success: 'text-emerald-600',
  default: 'text-gray-900',
};

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function remainingMetricTone(remainingQty) {
  return toFiniteNumber(remainingQty) > 0 ? 'alert' : 'success';
}

function deliveredMetricTone(line) {
  const deliveredQty = toFiniteNumber(getCustomerOrderLineDeliveredQty(line));
  const requiredQty = Math.max(
    0,
    toFiniteNumber(getCustomerOrderLineOrderQty(line))
      - toFiniteNumber(getCustomerOrderLineCancelQty(line)),
  );
  return deliveredQty >= requiredQty ? 'success' : 'alert';
}

/** 直式計算對照列：左標籤、右數值。 */
function MetricCell({ label, value, tone = 'default', emphasized = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs text-gray-600">
      <span className={cn(emphasized && 'font-semibold text-gray-700')}>
        {label}
        {emphasized ? ' ★' : ''}
      </span>
      <span
        className={cn(
          'text-base font-semibold tabular-nums',
          METRIC_TONE_CLASS[tone],
        )}
      >
        {value}
      </span>
    </div>
  );
}

MetricCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  tone: PropTypes.oneOf(['highlight', 'alert', 'success', 'default']),
  emphasized: PropTypes.bool,
};

function SummaryMetricCard({ title, children, className, bodyClassName }) {
  return (
    <div
      className={cn(
        'rounded border border-slate-200 bg-panel px-3 py-2',
        className,
      )}
    >
      <p className="mb-1.5 text-xs font-semibold text-gray-600">{title}</p>
      <div className={cn('flex flex-col gap-1.5', bodyClassName)}>
        {children}
      </div>
    </div>
  );
}

SummaryMetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
};

function resolveMaterialRowKey(item, index) {
  return String(item?.id ?? resolveMaterialSnapshotMtrId(item) ?? `mtr-${index}`);
}

const PURCHASE_MATERIAL_HINT = '請先勾選下方物料，再進行採購。';

/** 尾差為 0／未回傳時不顯示，避免每列多一行 0。 */
function hasRoundingAdjustment(line) {
  const value = Number(getCustomerOrderLineRoundingAdjustment(line));
  return Number.isFinite(value) && value !== 0;
}

function LineExpandedPanels({ line, customerOrderId, status }) {
  const navigate = useNavigate();
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [selectedMaterialKeys, setSelectedMaterialKeys] = useState(() => new Set());
  const salesOrderQty = getCustomerOrderLineSalesOrderQty(line);
  const remainingSalesOrderQty = getCustomerOrderLineRemainingQty(line);
  const deliveredQty = getCustomerOrderLineDeliveredQty(line);
  const remainingDeliveryQty = getCustomerOrderLineRemainingDeliveryQty(line);
  const remarkValue = line?.remark == null || line?.remark === ''
    ? '—'
    : String(line.remark);
  const materials = useMemo(
    () => getCustomerOrderLineMtrRequirements(line),
    [line],
  );
  const extraItems = useMemo(() => line?.extraItems ?? [], [line?.extraItems]);
  const hasSnapshots = materials.length > 0 || extraItems.length > 0;
  const canCreateManufacturingWorkOrders = (
    canCreateManufacturingWorkOrdersFromCustomerOrder(status)
  );
  const manufacturingUrl = canCreateManufacturingWorkOrders
    ? buildManufacturingMaterialsUrlFromCustomerOrderLine(customerOrderId, line)
    : null;

  const purchasableRowKeys = useMemo(
    () => materials
      .map((item, index) => ({
        item,
        rowKey: resolveMaterialRowKey(item, index),
      }))
      .filter(({ item }) => (
        isPurchasableMaterialSnapshot(item) && Boolean(resolveMaterialSnapshotMtrId(item))
      ))
      .map(({ rowKey }) => rowKey),
    [materials],
  );

  const allMaterialsSelected = purchasableRowKeys.length > 0
    && purchasableRowKeys.every((rowKey) => selectedMaterialKeys.has(rowKey));
  const someMaterialsSelected = purchasableRowKeys.some((rowKey) => (
    selectedMaterialKeys.has(rowKey)
  ));
  const selectAllChecked = allMaterialsSelected
    ? true
    : (someMaterialsSelected ? 'indeterminate' : false);

  const materialRows = materials.map((item, index) => {
    const rowKey = resolveMaterialRowKey(item, index);
    const mtrId = resolveMaterialSnapshotMtrId(item);
    const mtrNo = getMaterialSnapshotMtrNo(item);
    const mtrName = getMaterialSnapshotMtrName(item);
    const showPurchase = isPurchasableMaterialSnapshot(item) && Boolean(mtrId);
    const showWorkOrder = isManufacturableMaterialSnapshot(item)
      && Boolean(mtrId || mtrNo);

    return {
      id: rowKey,
      cells: [
        showPurchase ? (
          <Checkbox
            key={`select-${rowKey}`}
            checked={selectedMaterialKeys.has(rowKey)}
            aria-label={`選取 ${mtrNo || mtrName || rowKey}`}
            onCheckedChange={(checked) => {
              setSelectedMaterialKeys((prev) => {
                const next = new Set(prev);
                if (checked === true) {
                  next.add(rowKey);
                } else {
                  next.delete(rowKey);
                }
                return next;
              });
            }}
          />
        ) : (
          <span key={`select-${rowKey}`} className="text-muted-foreground">—</span>
        ),
        showWorkOrder ? (
          <Link
            key={`create-wo-${rowKey}`}
            to={buildWorkOrderCreateUrlFromMaterialSnapshot(line, item)}
            className="text-sm font-medium text-primary-600 hover:underline"
            onClick={(event) => event.stopPropagation()}
          >
            建立工單
          </Link>
        ) : (
          <span key={`create-wo-${rowKey}`} className="text-muted-foreground">—</span>
        ),
        <TruncatedTextWithTooltip
          key={`mtr-no-${rowKey}`}
          text={mtrNo || '—'}
          className="max-w-[10rem]"
        />,
        <TruncatedTextWithTooltip
          key={`mtr-name-${rowKey}`}
          text={mtrName || '—'}
          className="max-w-[12rem]"
        />,
        formatDisplayQty(getMaterialSnapshotQtyPerProduct(item)),
        displayOrDash(getMaterialSnapshotBaseUnitName(item)),
        displayOrDash(item.positionName),
        displayOrDash(item.remark),
      ],
    };
  });

  const extraRows = extraItems.map((item, index) => ({
    id: String(item.id ?? `extra-${index}`),
    cells: [
      <TruncatedTextWithTooltip
        key={`extra-name-${item.id ?? index}`}
        text={item.itemName || '—'}
        className="max-w-[14rem]"
      />,
      formatDisplayQty(getExtraItemQtyPerProduct(item)),
      formatDisplayQty(getExtraItemReferenceQty(item)),
      formatDisplayQty(getExtraItemDeliveredQty(item)),
      formatDisplayQty(getExtraItemRemainingReferenceQty(item)),
      displayOrDash(item.unitName),
      displayOrDash(item.remark),
    ],
  }));

  const selectedMaterials = materials.filter((item, index) => (
    selectedMaterialKeys.has(resolveMaterialRowKey(item, index))
  ));
  const hasSelectedMaterials = selectedMaterials.length > 0;
  const showPurchaseHint = !hasSelectedMaterials;

  function handleToggleAllMaterials(checked) {
    if (checked === true) {
      setSelectedMaterialKeys(new Set(purchasableRowKeys));
      return;
    }
    setSelectedMaterialKeys(new Set());
  }

  function handleSnapshotOpenChange(nextOpen) {
    setSnapshotOpen(nextOpen);
    if (!nextOpen) {
      setSelectedMaterialKeys(new Set());
    }
  }

  function handlePurchaseSelected() {
    if (selectedMaterials.length === 0) {
      return;
    }
    navigate(buildPurchaseOrderCreateUrlFromMaterialSnapshots(line, selectedMaterials));
    setSnapshotOpen(false);
    setSelectedMaterialKeys(new Set());
  }

  function handleOpenSnapshot(event) {
    event.stopPropagation();
    if (!hasSnapshots) {
      return;
    }
    setSnapshotOpen(true);
  }

  return (
    <>
      <div className="space-y-4 border-t border-slate-200 bg-slate-50/50 px-3 py-3">
        <div
          className={cn(
            'grid grid-cols-1 gap-3',
            'sm:grid-cols-2 sm:items-end',
          )}
        >
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-medium text-gray-600">備註</p>
            <p className="text-sm wrap-break-word text-gray-900">
              {remarkValue}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              size="sm"
              disabled={!hasSnapshots}
              onClick={handleOpenSnapshot}
            >
              物料快照
            </Button>
            {canCreateManufacturingWorkOrders ? (
              manufacturingUrl ? (
                <Button type="button" size="sm" variant="outline" asChild>
                  <Link
                    to={manufacturingUrl}
                    onClick={(event) => event.stopPropagation()}
                  >
                    建立多階層工單
                  </Link>
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled
                  title="此明細缺少 line id"
                >
                  建立多階層工單
                </Button>
              )
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:items-stretch">
          <SummaryMetricCard title="數量" className="h-full">
            <MetricCell
              label="訂購"
              value={formatDisplayQty(getCustomerOrderLineOrderQty(line))}
              tone="highlight"
              emphasized
            />
            <MetricCell
              label="取消"
              value={formatDisplayQty(getCustomerOrderLineCancelQty(line))}
            />
            <MetricCell
              label="已開銷貨"
              value={formatDisplayQty(salesOrderQty)}
            />
            <MetricCell
              label="可開銷貨"
              value={formatDisplayQty(remainingSalesOrderQty)}
              tone={remainingMetricTone(remainingSalesOrderQty)}
            />
            <div className="my-0.5 border-t border-dashed border-slate-200" />
            <MetricCell
              label="已送達"
              value={formatDisplayQty(deliveredQty)}
              tone={deliveredMetricTone(line)}
            />
            <MetricCell
              label="未送達"
              value={formatDisplayQty(remainingDeliveryQty)}
              tone={remainingMetricTone(remainingDeliveryQty)}
            />
          </SummaryMetricCard>
          <SummaryMetricCard
            title="金額"
            className="flex h-full flex-col"
            bodyClassName="flex flex-1 flex-col gap-1.5"
          >
            <div className="flex flex-col gap-1.5">
              <MetricCell
                label="單價"
                value={formatDisplayAmount(getCustomerOrderLineUnitPrice(line))}
              />
              <MetricCell
                label="折數"
                value={(() => {
                  const rate = getCustomerOrderLineDiscountRate(line);
                  return rate == null ? '—' : rate;
                })()}
              />
              <MetricCell
                label="每件折扣"
                value={formatDisplayAmount(getCustomerOrderLineUnitDiscountAmount(line))}
              />
              <MetricCell
                label="折抵"
                value={formatDisplayAmount(getCustomerOrderLineDiscountTotalAmount(line))}
              />
              {hasRoundingAdjustment(line) ? (
                <MetricCell
                  label="尾差"
                  value={formatDisplayAmount(getCustomerOrderLineRoundingAdjustment(line))}
                />
              ) : null}
            </div>
            <div className="mt-auto border-t border-dashed border-slate-200 pt-1.5">
              <MetricCell
                label="小計"
                value={formatDisplayAmount(getCustomerOrderLineSubtotalAmount(line))}
                tone="highlight"
                emphasized
              />
            </div>
          </SummaryMetricCard>
        </div>
      </div>

      <AppDialog
        open={snapshotOpen}
        onOpenChange={handleSnapshotOpenChange}
        title="物件與配件快照"
        description="核准時的物料組成與配件快照。"
        headerVariant="muted"
        contentClassName={dialogContentCompactGapClassName}
        maxWidthClassName="sm:max-w-3xl"
        closeOnOutsideClick={false}
        onCancel={() => handleSnapshotOpenChange(false)}
        cancelText="關閉"
      >
        <div className="space-y-5">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <p className="mb-0! text-sm font-semibold text-gray-800">物料組成</p>
              <Button
                type="button"
                size="sm"
                disabled={!hasSelectedMaterials && !showPurchaseHint}
                className={cn(showPurchaseHint && 'opacity-50')}
                onClick={handlePurchaseSelected}
              >
                <span className="inline-flex items-center gap-0.5">
                  採購物料
                  {showPurchaseHint ? (
                    <ActionHintIcon
                      tip={PURCHASE_MATERIAL_HINT}
                      ariaLabel="採購物料說明"
                    />
                  ) : null}
                </span>
              </Button>
            </div>
            <MiniTable
              headers={['選取', '操作', '料號', '料名', '單位用量', '單位', '部位', '備註']}
              headerCells={[
                purchasableRowKeys.length > 0 ? (
                  <Checkbox
                    key="select-all-materials"
                    checked={selectAllChecked}
                    aria-label="全選物料"
                    onCheckedChange={handleToggleAllMaterials}
                  />
                ) : null,
              ]}
              rows={materialRows}
              emptyText="尚無資料"
            />
          </div>
          <div>
            <p className="mb-2 text-sm font-semibold text-gray-800">配件</p>
            <MiniTable
              headers={['品名', '單位用量', '參考量', '已交', '剩餘', '單位', '備註']}
              rows={extraRows}
              emptyText="尚無資料"
            />
          </div>
        </div>
      </AppDialog>
    </>
  );
}

LineExpandedPanels.propTypes = {
  line: PropTypes.object.isRequired,
  customerOrderId: PropTypes.string,
  status: PropTypes.string,
};

function HeaderKeyMetric({ label, value, tone = 'default', emphasized = false }) {
  return (
    <div className="min-w-0 text-right">
      <p className="text-xs text-gray-500">
        {label}
        {emphasized ? ' ★' : ''}
      </p>
      <p
        className={cn(
          'text-base font-semibold tabular-nums',
          METRIC_TONE_CLASS[tone],
        )}
      >
        {value}
      </p>
    </div>
  );
}

HeaderKeyMetric.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  tone: PropTypes.oneOf(['highlight', 'alert', 'success', 'default']),
  emphasized: PropTypes.bool,
};

function LineSummaryRow({ line, lineIndex, expanded, onToggle }) {
  const product = line?.product;
  const unitHint = product?.salesUnitName
    ? `（${product.salesUnitName}）`
    : '';
  const remainingSalesOrderQty = getCustomerOrderLineRemainingQty(line);

  return (
    <div
      className={cn(
        'grid w-full cursor-pointer grid-cols-[3rem_minmax(0,1fr)] gap-2 px-3 py-3 text-left',
        'hover:bg-slate-50',
        expanded && 'bg-slate-50',
      )}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
    >
      <div className="flex items-start gap-2 pt-2.5">
        <ChevronDown
          size={16}
          className={cn(
            'mt-0.5 text-gray-500 shortcut_transition-transform',
            !expanded && '-rotate-90',
          )}
        />
        <span className="text-sm font-semibold text-gray-900">
          #
          {lineIndex + 1}
        </span>
      </div>
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex min-w-0 flex-wrap items-center gap-1 text-sm text-gray-900">
            <MaterialNameStack
              name={product?.name}
              code={product?.productNo}
            />
            {unitHint ? (
              <span className="text-xs text-gray-500">{unitHint}</span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-start justify-end gap-x-4 gap-y-1">
          <HeaderKeyMetric
            label="訂購"
            value={formatDisplayQty(getCustomerOrderLineOrderQty(line))}
            tone="highlight"
            emphasized
          />
          <HeaderKeyMetric
            label="可開銷貨"
            value={formatDisplayQty(remainingSalesOrderQty)}
            tone={remainingMetricTone(remainingSalesOrderQty)}
          />
          <HeaderKeyMetric
            label="小計"
            value={formatDisplayAmount(getCustomerOrderLineSubtotalAmount(line))}
            tone="highlight"
            emphasized
          />
        </div>
      </div>
    </div>
  );
}

LineSummaryRow.propTypes = {
  line: PropTypes.object.isRequired,
  lineIndex: PropTypes.number.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

function resolveManualLineAmount(line) {
  const fromAmount = getCustomerOrderLineSubtotalAmount(line);
  if (fromAmount != null && fromAmount !== '') {
    return fromAmount;
  }
  return null;
}

function ManualLineSummaryRow({ line, lineIndex }) {
  const unitHint = line?.unitName ? `（${line.unitName}）` : '';
  const amount = resolveManualLineAmount(line);

  return (
    <div
      className={cn(
        'grid w-full grid-cols-[3rem_minmax(0,1fr)] gap-2 px-3 py-3 text-left',
      )}
    >
      <div className="flex items-start pt-1">
        <span className="text-sm font-semibold text-gray-900">
          #
          {lineIndex + 1}
        </span>
      </div>
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex min-w-0 flex-wrap items-center gap-1 text-sm text-gray-900">
            <TruncatedTextWithTooltip text={displayOrDash(line?.itemName)} />
            {unitHint ? (
              <span className="text-xs text-gray-500">{unitHint}</span>
            ) : null}
          </div>
          {line?.remark ? (
            <p className="text-xs text-muted-foreground">
              {line.remark}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-start justify-end gap-x-4 gap-y-1">
          <HeaderKeyMetric
            label="訂購"
            value={formatDisplayQty(getCustomerOrderLineOrderQty(line))}
            tone="highlight"
            emphasized
          />
          <HeaderKeyMetric
            label="取消"
            value={formatDisplayQty(getCustomerOrderLineCancelQty(line))}
          />
          <HeaderKeyMetric
            label="單價"
            value={formatDisplayAmount(getCustomerOrderLineUnitPrice(line))}
          />
          {hasRoundingAdjustment(line) ? (
            <HeaderKeyMetric
              label="尾差"
              value={formatDisplayAmount(getCustomerOrderLineRoundingAdjustment(line))}
            />
          ) : null}
          <HeaderKeyMetric
            label="金額"
            value={formatDisplayAmount(amount)}
            tone="highlight"
            emphasized
          />
        </div>
      </div>
    </div>
  );
}

ManualLineSummaryRow.propTypes = {
  line: PropTypes.object.isRequired,
  lineIndex: PropTypes.number.isRequired,
};

const CustomerOrderLinesSection = ({
  lines = [],
  manualLines = [],
  customerOrderId,
  status,
}) => {
  const rows = useMemo(
    () => (lines ?? []).map((line, index) => ({
      ...line,
      _rowId: String(line?.id ?? `co-line-${index}`),
    })),
    [lines],
  );
  const manualRows = useMemo(
    () => (manualLines ?? []).map((line, index) => ({
      ...line,
      _rowId: String(line?.id ?? `co-manual-line-${index}`),
    })),
    [manualLines],
  );

  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = useCallback((rowId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  if (rows.length === 0 && manualRows.length === 0) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">尚無資料</p>;
  }

  return (
    <div className="divide-y divide-slate-200">
      {rows.map((line, index) => {
        const expanded = expandedIds.has(line._rowId);
        return (
          <div
            key={line._rowId}
            className={cn(
              expanded && 'border-l-2 border-l-primary-400 bg-slate-50/40',
            )}
          >
            <LineSummaryRow
              line={line}
              lineIndex={index}
              expanded={expanded}
              onToggle={() => toggleExpanded(line._rowId)}
            />
            <div
              className={cn(
                'grid shortcut_transition-disclosure',
                expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
              )}
              aria-hidden={!expanded}
            >
              <div className="min-h-0 overflow-hidden">
                <LineExpandedPanels
                  line={line}
                  customerOrderId={customerOrderId}
                  status={status}
                />
              </div>
            </div>
          </div>
        );
      })}
      {manualRows.map((line, index) => (
        <ManualLineSummaryRow
          key={line._rowId}
          line={line}
          lineIndex={rows.length + index}
        />
      ))}
    </div>
  );
};

CustomerOrderLinesSection.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.object),
  manualLines: PropTypes.arrayOf(PropTypes.object),
  customerOrderId: PropTypes.string,
  status: PropTypes.string,
};

export default CustomerOrderLinesSection;
